const MANUAL_SUBSTANCE_DECLARATION_POLICY = Object.freeze({
    strategy: 'manual-v1',
})

const createThresholdPolicy = () => Object.freeze({
    strategy: 'potential-exceedance-threshold-v1',
    threshold: Object.freeze({
        value: 0.1,
        unit: '% w/w',
    }),
    missingValueOutcome: 'below',
    boundaryOutcome: 'claim-dependent',
    aggregation: 'any-above-otherwise-below',
    requiredForCompletion: true,
    reconciliation: Object.freeze({
        onConflict: 'block',
    }),
    emptySubstancesFallback: Object.freeze({
        sourceClaim: 'compliant',
        claimType: 'does_not_contain_restricted_substances',
    }),
})

const LEGISLATION_REVIEW_POLICIES = Object.freeze({
    reach: createThresholdPolicy(),
    rohs: createThresholdPolicy(),
})

const DISABLED_MANUAL_SUBSTANCE_POLICY = Object.freeze({
    matchPrefill: Object.freeze({
        mode: 'disabled',
        fields: Object.freeze([]),
    }),
    defaultConcentrationBasis: null,
})

const createManualSubstancePolicy = () => Object.freeze({
    matchPrefill: Object.freeze({
        mode: 'prefill-always',
        fields: Object.freeze(['name', 'casNumber', 'ecNumber']),
    }),
    defaultConcentrationBasis: 'w/w',
})

const MANUAL_SUBSTANCE_POLICIES = Object.freeze({
    reach: createManualSubstancePolicy(),
    rohs: createManualSubstancePolicy(),
})

const SUPPORTED_STRATEGIES = Object.freeze([
    'manual-v1',
    'concentration-threshold-v1',
    'potential-exceedance-threshold-v1',
    'presence-v1',
])
const THRESHOLD_STRATEGIES = Object.freeze([
    'concentration-threshold-v1',
    'potential-exceedance-threshold-v1',
])
const SUPPORTED_THRESHOLD_UNITS = Object.freeze(['%', '%w/w', 'percent', 'percentw/w', 'w/w'])
const SUPPORTED_EMPTY_SUBSTANCES_SOURCE_CLAIMS = Object.freeze(['compliant'])
const SUPPORTED_EMPTY_SUBSTANCES_CLAIMS = Object.freeze([
    'does_not_contain_restricted_substances',
])
const SUPPORTED_MATCH_PREFILL_MODES = Object.freeze([
    'disabled',
    'prefill-once',
    'prefill-always',
])
const SUPPORTED_MATCH_PREFILL_FIELDS = Object.freeze(['name', 'casNumber', 'ecNumber'])
const SUPPORTED_CONCENTRATION_BASES = Object.freeze(['w/w', 'w/v', 'v/v', 'other'])

const normalizeLegislationKey = value => (
    typeof value === 'string' ? value.trim().toLowerCase() : ''
)

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

const normalizeToken = value => (
    typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '') : ''
)

const isValidEmptySubstancesFallback = fallback => (
    typeof fallback === 'undefined'
    || (isObject(fallback)
        && SUPPORTED_EMPTY_SUBSTANCES_SOURCE_CLAIMS.includes(fallback.sourceClaim)
        && SUPPORTED_EMPTY_SUBSTANCES_CLAIMS.includes(fallback.claimType))
)

const isValidReconciliation = reconciliation => (
    typeof reconciliation === 'undefined'
    || (isObject(reconciliation)
        && ['advisory', 'block'].includes(reconciliation.onConflict))
)

const isValidPolicy = policy => {
    if (!isObject(policy)
        || !SUPPORTED_STRATEGIES.includes(policy.strategy)
        || (typeof policy.requiredForCompletion !== 'undefined'
            && typeof policy.requiredForCompletion !== 'boolean')
        || !isValidReconciliation(policy.reconciliation)
        || !isValidEmptySubstancesFallback(policy.emptySubstancesFallback)) {
        return false
    }

    if (!THRESHOLD_STRATEGIES.includes(policy.strategy)) {
        return true
    }

    return isObject(policy.threshold)
        && Number.isFinite(Number(policy.threshold.value))
        && SUPPORTED_THRESHOLD_UNITS.includes(normalizeToken(policy.threshold.unit))
        && ['below', 'ambiguous'].includes(policy.missingValueOutcome)
        && ['above', 'below', 'claim-dependent'].includes(policy.boundaryOutcome)
        && policy.aggregation === 'any-above-otherwise-below'
}

const isValidManualSubstancePolicy = policy => {
    const prefill = policy && policy.matchPrefill
    const fields = prefill && prefill.fields

    return isObject(policy)
        && isObject(prefill)
        && SUPPORTED_MATCH_PREFILL_MODES.includes(prefill.mode)
        && Array.isArray(fields)
        && fields.every(field => SUPPORTED_MATCH_PREFILL_FIELDS.includes(field))
        && (policy.defaultConcentrationBasis === null
            || SUPPORTED_CONCENTRATION_BASES.includes(policy.defaultConcentrationBasis))
}

const suppliedSubstanceDeclarationPolicy = legislation => {
    const reviewPolicy = legislation && isObject(legislation.reviewPolicy)
        ? legislation.reviewPolicy
        : null

    if (!reviewPolicy
        || !Object.prototype.hasOwnProperty.call(reviewPolicy, 'substanceDeclaration')) {
        return { supplied: false, policy: null }
    }

    return {
        supplied: true,
        policy: reviewPolicy.substanceDeclaration,
    }
}

const suppliedManualSubstancePolicy = legislation => {
    const reviewPolicy = legislation && isObject(legislation.reviewPolicy)
        ? legislation.reviewPolicy
        : null

    if (!reviewPolicy
        || !Object.prototype.hasOwnProperty.call(reviewPolicy, 'manualSubstance')) {
        return { supplied: false, policy: null }
    }

    return {
        supplied: true,
        policy: reviewPolicy.manualSubstance,
    }
}

const createLegislationReviewPolicyProvider = ({
    policies = LEGISLATION_REVIEW_POLICIES,
    manualSubstancePolicies = MANUAL_SUBSTANCE_POLICIES,
} = {}) => Object.freeze({
    resolveSubstanceDeclaration(legislation = {}) {
        const supplied = suppliedSubstanceDeclarationPolicy(legislation)
        if (supplied.supplied) {
            return isValidPolicy(supplied.policy)
                ? { source: 'backend', policy: supplied.policy }
                : { source: 'backend-invalid', policy: MANUAL_SUBSTANCE_DECLARATION_POLICY }
        }

        const fallback = policies[normalizeLegislationKey(legislation.key)]
        return isValidPolicy(fallback)
            ? { source: 'frontend-registry', policy: fallback }
            : { source: 'default', policy: MANUAL_SUBSTANCE_DECLARATION_POLICY }
    },
    resolveManualSubstance(legislation = {}) {
        const supplied = suppliedManualSubstancePolicy(legislation)
        if (supplied.supplied) {
            return isValidManualSubstancePolicy(supplied.policy)
                ? { source: 'backend', policy: supplied.policy }
                : { source: 'backend-invalid', policy: DISABLED_MANUAL_SUBSTANCE_POLICY }
        }

        const fallback = manualSubstancePolicies[normalizeLegislationKey(legislation.key)]
        return isValidManualSubstancePolicy(fallback)
            ? { source: 'frontend-registry', policy: fallback }
            : { source: 'default', policy: DISABLED_MANUAL_SUBSTANCE_POLICY }
    },
})

const legislationReviewPolicyProvider = createLegislationReviewPolicyProvider()

export {
    LEGISLATION_REVIEW_POLICIES,
    MANUAL_SUBSTANCE_POLICIES,
    DISABLED_MANUAL_SUBSTANCE_POLICY,
    MANUAL_SUBSTANCE_DECLARATION_POLICY,
    createLegislationReviewPolicyProvider,
    legislationReviewPolicyProvider,
}

export default legislationReviewPolicyProvider
